import {
    collection,
    component,
    EntityData,
    field,
    identifier,
    LocalisableEntityData,
    reloadable,
    transientIdentifier
} from "@fedeltapointofsale/data-client";
import {PriceLevelReferenceData} from "../price-level/price-level.data";
import {MembershipTypeReferenceData} from "../../customers/membership-type/membership-type.data";
import {MixAndMatchComplexity} from "./mix-and-match-complexity-analysis.data";
import {ExtraReferenceData} from "../../catalogue/extras/extra.data";
import {PricingScheduleSummaryData} from "../pricing-schedule/pricing-schedule.data";
import {TypedCustomObjectReferenceData} from "../../custom-objects/custom-object/custom-object.data";
import {PeriodScheduleReferenceData} from "../../system/period-schedule/period-schedule.data";
import {TypedTerminalReferenceData} from "../../pos/terminal/terminal-reference.data";

export class MixAndMatchInclusionSellableData extends EntityData<MixAndMatchInclusionSellableData> {
    @field sellableType: string;
    @identifier sellableId: number;
    @field name: string;
    @field price: number;
    @field plu: string;
}

export class MixAndMatchInclusionData extends EntityData<MixAndMatchInclusionData> {
    @component sellable: MixAndMatchInclusionSellableData;
    @component customObject: TypedCustomObjectReferenceData;
    @field required: boolean = false;
    @field minProductQty: number;
    @field maxProductQty: number;
    @component extra: ExtraReferenceData;
    @reloadable @component extraCustomObject: TypedCustomObjectReferenceData;
    @field minExtraQty: number;
    @field maxExtraQty: number;
    @field discountableQty: number;
    @field discountableExtraQty: number;
    @field includeCompositeComponents: boolean;
    @component discountableExtra: ExtraReferenceData;
    @reloadable @component discountableExtraCustomObject: TypedCustomObjectReferenceData;
    intersectionComplexity: MixAndMatchComplexity;

    @transientIdentifier
    get id(): string {
        return this.sellable && this.sellable.sellableId ? "sellable_" + this.sellable.sellableId
            : this.customObject && this.customObject.customObjectId ? "customObject_" + this.customObject.customObjectId
                : undefined;
    }

    get name(): string {
        return this.sellable && this.sellable.sellableId ? this.sellable.name
            : this.customObject && this.customObject.customObjectId ? this.customObject.name
                : undefined;
    }

    get detail(): string {
        return this.sellable && this.sellable.sellableId ? this.sellable.plu
            : this.customObject && this.customObject.customObjectId ? this.customObject.customObjectType.name
                : undefined;
    }

    get price(): number {
        return this.sellable && this.sellable.sellableId ? this.sellable.price : undefined;
    }

    get extraName(): string {
        return this.extra && this.extra.id ? this.extra.name
            : this.extraCustomObject && this.extraCustomObject.customObjectId ? this.extraCustomObject.name
                : undefined;
    }
}

export class MixAndMatchLoyaltyData extends EntityData<MixAndMatchLoyaltyData> {
    @identifier id: number;
    @collection(MembershipTypeReferenceData) mixAndMatchMemberTypes: MembershipTypeReferenceData[];
    @field includeGuests: boolean = false;
}

export class MixAndMatchScheduleSummaryData extends PricingScheduleSummaryData<MixAndMatchScheduleSummaryData> {
    @identifier mixAndMatchScheduleId: number;
    @field name: string;
    @field discontinued: boolean;
}

export enum MixAndMatchType {
    COMBINATION_PRICE = 1,
    COMBINATION_DISCOUNT,
    DISCOUNT_ALL_ITEMS,
    DISCOUNT_CHEAPEST,
    PRICE_LEVEL,
    DISCOUNT_MOST_EXPENSIVE
}

export class MixAndMatchData extends LocalisableEntityData<MixAndMatchData> {
    @identifier id: number;
    @field name: string;
    @field description: string;
    @field externalDescription: string;
    @field priority: number;
    @field type: MixAndMatchType = MixAndMatchType.COMBINATION_PRICE;
    @field triggerQty: number;
    @field amount: number;
    @component priceLevel: PriceLevelReferenceData;
    @field doNotAutomaticallyApply: boolean = false;
    @field applyAcrossCourses: boolean = true;
    @field applyToAllItemsAfterTrigger: boolean;
    @field discountItem: boolean = true;
    @field discountExtras: boolean = false;
    @field consumeMostExpensiveFirst: boolean;
    @field trxLimit: number;
    @field discontinued: boolean;
    @collection(TypedTerminalReferenceData) terminals: TypedTerminalReferenceData[];
    @collection(MixAndMatchInclusionData) inclusions: MixAndMatchInclusionData[];
    @component memberLoyalty: MixAndMatchLoyaltyData;
    @collection(PeriodScheduleReferenceData) periodSchedules: PeriodScheduleReferenceData[];
    @collection(MixAndMatchScheduleSummaryData) schedules: MixAndMatchScheduleSummaryData[];

    applyToAllProducts: boolean = undefined;
}

export class MixAndMatchReferenceData extends LocalisableEntityData<MixAndMatchReferenceData> {
    @identifier id: number;
    @field name: string;
}
